import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { MediasService } from 'src/app/shared/services/medias.service';
import { StrategiesService } from 'src/app/shared/strategies/strategies.service';
import { StrategyAbstractService } from 'src/app/shared/strategies/strategy-abstract.service';

import { Subscription } from 'rxjs';
import { JointDiagramContext } from '../../../models';
import { CtaTextEnum, RecurringNotif } from '../models';
import { OptinsService } from 'src/app/marketing/optins/optins.service';

@Component({
  selector: 'app-recurring-notif-dialog',
  templateUrl: './recurring-notif-dialog.component.html',
  styleUrls: ['./recurring-notif-dialog.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RecurringNotifDialogComponent implements OnInit, OnDestroy {
  optinItems: Choice[];
  pictureUrl = '';
  strategy: StrategyAbstractService;
  elementData: any;
  loading = false;
  ctaTextChoices: Choice[];

  form = this.formBuilder.group({
    title: ['', [Validators.required]],
    optinId: ['', [Validators.required]],
    ctaText: [{ value: CtaTextEnum.getUpdates, disabled: true }],
  });

  private subscriptions = new Subscription();

  constructor(
    private basicService: BasicService,
    private dialogRef: MatDialogRef<RecurringNotifDialogComponent>,
    private formBuilder: FormBuilder,
    private mediaService: MediasService,
    private strategiesService: StrategiesService,
    private optinsService: OptinsService,
    @Inject(MAT_DIALOG_DATA) public data: { context: JointDiagramContext; elementData: RecurringNotif },
    @Inject('DropdownItemsFactory') private dropdownItemsService: any
  ) {
    this.strategy = this.strategiesService.getStrategyFromType(this.data.context.site.type);
  }

  ngOnInit(): void {
    if (this.data.elementData) {
      const { title, optinId, ctaText } = this.data.elementData;
      this.form.setValue({ title, optinId, ctaText: ctaText || CtaTextEnum.getUpdates });
    } else {
      this.data.elementData = {
        frequency: null,
        frequencyLabel: null,
        optinId: null,
        optinLabel: null,
        title: null,
      };
    }

    this.form.get('title').disable();

    this.subscriptions.add(
      this.form.get('optinId').valueChanges.subscribe(value => {
        const optinFound = this.data.context.optins.find(({ id }) => id === value);

        if (optinFound?.title) {
          this.form.get('title').setValue(optinFound.title, { emitEvent: false });
        }
      })
    );

    // facebook only
    if (this.strategy.getType() === 1) {
      this.form.get('ctaText').enable();

      const translate = (key: string) => {
        const prefix = 'parameters/quick_replies/details/graph/edit_recurring_notif_box/cta_text/';
        return this.basicService.translate(`${prefix}${key.toLocaleLowerCase()}`);
      };

      this.ctaTextChoices = [
        { id: CtaTextEnum.allow, label: translate(CtaTextEnum.allow) },
        { id: CtaTextEnum.get, label: translate(CtaTextEnum.get) },
        { id: CtaTextEnum.getUpdates, label: translate(CtaTextEnum.getUpdates) },
        { id: CtaTextEnum.optIn, label: translate(CtaTextEnum.optIn) },
        { id: CtaTextEnum.signUp, label: translate(CtaTextEnum.signUp) },
      ];
    }

    const rnOptins = this.data.context.optins.filter(optin =>
      this.optinsService.OPTIN_TYPE_PER_SOCIAL_MEDIA[this.data.context.site.type_name].includes(optin.type)
    );
    this.optinItems = this.dropdownItemsService.buildOptinItems(rnOptins);

    this.updatePictureUrl();
  }

  save() {
    const { optinId, ctaText, title } = this.form.value;
    this.data.elementData = {
      ...this.data.elementData,
      title,
      optinId,
      ctaText,
      frequency: 'daily',
      frequencyLabel: this.basicService.translate(
        'parameters/quick_replies/details/graph/edit_recurring_notif_box_frequency_daily'
      ),
    };

    this.data.context.optins.forEach(({ id, name, title }) => {
      if (id === this.data.elementData.optinId) {
        // Store label, to be displayed it in the bobox
        this.data.elementData.optinLabel = name;
        this.data.elementData.title = title;
      }
    });

    this.dialogRef.close(this.data.elementData);
  }

  updatePictureUrl() {
    this.pictureUrl = this.mediaService.getPictureUrl(this.data?.elementData?.pictureId);
  }

  uploadImage(event: Event) {
    this.removeImage();
    this.loading = true;

    this.mediaService.checkLimitationsAndUpload(
      {
        siteId: this.data.context.site.id,
        mediaType: 'image',
        file: (event.target as HTMLInputElement).files[0],
        mediaLimitations: this.strategy.getQuickRepliesElementsConfig(),
        uploadAsync: false,
      },
      ({ data }) => {
        this.data.elementData.pictureId = data.media_id;
        this.loading = false;
        this.updatePictureUrl();
      },
      ({ data }) => {
        this.loading = false;
        this.basicService.toastAlert(data.error.reason);
      }
    );
  }

  removeImage() {
    delete this.data.elementData.pictureId;
    this.updatePictureUrl();
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
